import ScratchJr from '../../../ScratchJr';
import Palette from '../../../ui/Palette';
import Thumbs from '../../../ui/Thumbs';
import ActivityStorageService from '../../storage/ActivityStorageService';
import ActivityAudio from '../../shared/ActivityAudio';
import {scorePreferredNarrationVoice} from '../../shared/SpeechVoicePreference';
import PaletteRestrictor from '../guided-creation/PaletteRestrictor';
import {gn} from '../../../../utils/lib';
import PostPreviewUpsell from '../../shared/PostPreviewUpsell';
import CompletionModal from '../../shared/CompletionModal';
import ActivityState from '../../../../../../web/ai/activity-state/ActivityState';
import {evaluateSuccessCriteria} from '../../../../../../web/ai/activity-success';

const DEFAULT_PHASE = 'INTRO';
export const PHASES = Object.freeze({
    INTRO: 'INTRO',
    GROW_TRICK: 'GROW_TRICK',
    HIDE_TRICK: 'HIDE_TRICK',
    SHOW_TRICK: 'SHOW_TRICK',
    BIRD_SAY: 'BIRD_SAY',
    CREATIVE: 'CREATIVE',
    COMPLETE: 'COMPLETE'
});

const MEMBERS = ['cat', 'bird'];

export default class MagicShowFlow {
    constructor (activityData, stateMachine, container) {
        this._activityData = activityData;
        this._stateMachine = stateMachine;
        this._container = container;
        this._stage = container.querySelector('.stage') || container;
        this._paletteRestrictor = null;
        this._toast = null;
        this._doneBtn = null;
        this._ghostHand = null;
        this._phase = DEFAULT_PHASE;
        this._config = this._buildConfig(activityData);
        this._idleTimers = [];
        this._phaseTimeout = null;
        this._lastInteractionAt = Date.now();
        this._sessionStartedAt = Date.now();
        this._idleTimeMs = 0;
        this._idlePausedAt = null;
        this._selectedRole = null;
        this._hintEvents = [];
        this._voiceHistory = [];
        this._lastNarrationId = null;
        this._lastNarrationText = '';
        this._speechVoice = null;
        this._speechVoiceScore = 0;
        this._speechQueue = Promise.resolve();
        this._speechRunId = 0;
        this._interactionSettleTimer = null;
        this._voicesChangeHandler = this._refreshPreferredVoice.bind(this);
        this._destroyed = false;
        this._scriptSignatures = {};
        this._flagTapCount = 0;
        this._milestones = {
            catFlag: false,
            catGrow: false,
            catHide: false,
            catShow: false,
            birdSay: false
        };
        this._onPointerDown = this._handleMeaningfulInteraction.bind(this);
        this._onVisibilityChange = this._stopNarration.bind(this);
        this._onPageHide = this._stopNarration.bind(this);
    }

    async init () {
        this._paletteRestrictor = new PaletteRestrictor(this._container, []);
        this._attachListeners();
        this._initVoiceSelection();
        await this._waitForPreferredVoice();
        this._refreshScriptSignatures();

        const saved = await ActivityStorageService.getActivityProgress(this._activityData.activityId);
        if (saved && Array.isArray(saved.hintEvents)) {
            this._hintEvents = saved.hintEvents.slice();
        }
        if (saved && typeof saved.flagTapCount === 'number') {
            this._flagTapCount = saved.flagTapCount;
        }
        if (saved && saved.milestones) {
            this._milestones = {...this._milestones, ...saved.milestones};
        }
        const restoredScripts = this._restoreSavedScripts(saved);

        const savedPhase = saved && saved.currentState && PHASES[saved.currentState]
            ? saved.currentState
            : null;
        if (savedPhase) {
            const resumePhase = savedPhase === PHASES.COMPLETE ? PHASES.CREATIVE : savedPhase;
            this._setPhase(resumePhase, {silent: true});
            this._ensureExpectedSelection();
            this._syncVisualState();
            this._startIdleScaffolding();
            if (resumePhase === PHASES.CREATIVE) {
                this._showDoneButton();
            }
            this._queueNarration('VO-INTRO', this._buildResumePrompt(resumePhase), {
                speaker: this._speakerForPhase(resumePhase),
                clear: true
            }).then(() => {
                if (!this._destroyed && this._phase === PHASES.INTRO) {
                    this._advanceFromIntro();
                }
            });
            await this._persistProgress();
            return;
        }

        this._setPhase(PHASES.INTRO, {silent: true});
        this._selectRole('cat');
        this._syncVisualState();
        this._queueNarration('VO-INTRO', this._config.voiceOver['VO-INTRO'], {speaker: 'cat', clear: true})
            .then(() => {
                if (!this._destroyed && this._phase === PHASES.INTRO) {
                    return new Promise(resolve => setTimeout(resolve, 2000));
                }
            })
            .then(() => {
                if (!this._destroyed && this._phase === PHASES.INTRO) {
                    this._advanceFromIntro();
                }
            });
        await this._persistProgress();
    }

    onStateChange (prevState, newState) {
        if (prevState === 'SUCCESS' && newState === 'BUILDING') {
            this._resetForReplay();
            return;
        }

        if (newState === 'RUNNING') {
            this._flagTapCount++;
            this._clearIdleTimers();
            return;
        }

        if (newState === 'VALIDATING') {
            this._validateRun();
            return;
        }

        if (newState === 'BUILDING' || newState === 'READY') {
            this._syncVisualState();
            this._startIdleScaffolding();
        }
    }

    getBlockCount () {
        return ScratchJr.collectActivityBlocks().length;
    }

    evaluate () {
        const simState = this._buildSimState();
        const shared = evaluateSuccessCriteria(this._activityData, simState);
        return Object.assign({}, shared, {
            usedBlocks: this.getBlockCount(),
            storyPhase: this._phase,
            flagTapCount: this._flagTapCount
        });
    }

    _buildSimState () {
        return {
            scripts: {
                cat: this._getSpriteBlocks('cat')
            },
            phaseFlags: {
                completed: this._phase === PHASES.COMPLETE,
                storyPhase: this._phase,
                flagTapCount: this._flagTapCount
            }
        };
    }

    shouldResetSpritesBeforeRun () {
        return true;
    }

    shouldEvaluateWhileThreadsRunning () {
        return false;
    }

    showHint () {
        this._deliverIdleScaffold(1);
    }

    captureRunStartPosition () {
        // ScratchJr handles sprite reset for each green-flag run.
    }

    restartActivity () {
        if (this._stateMachine && this._stateMachine.state !== 'BUILDING') {
            this._stateMachine.transition('BUILDING');
        }
        this._resetForReplay();
        return true;
    }

    destroy () {
        this._destroyed = true;
        this._clearIdleTimers();
        if (this._interactionSettleTimer) {
            clearTimeout(this._interactionSettleTimer);
            this._interactionSettleTimer = null;
        }
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
        this._clearHintVisuals();
        this._hideDoneButton();
        CompletionModal.dismiss();
        this._closeAllBubbles();
        this._stopNarration();
        if (window.speechSynthesis && typeof window.speechSynthesis.removeEventListener === 'function') {
            window.speechSynthesis.removeEventListener('voiceschanged', this._voicesChangeHandler);
        }
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        window.removeEventListener('pagehide', this._onPageHide);
        window.removeEventListener('beforeunload', this._onPageHide);
        document.removeEventListener('pointerdown', this._onPointerDown, true);
        if (this._paletteRestrictor) {
            this._paletteRestrictor.destroy();
            this._paletteRestrictor = null;
        }
    }

    _buildConfig (activityData) {
        const magicShow = activityData.magicShow || {};
        const firstPage = activityData.projectTemplate && activityData.projectTemplate.pages
            ? activityData.projectTemplate.pages[0]
            : {};
        const sprites = Array.isArray(firstPage.sprites) ? firstPage.sprites : [];
        const byName = sprites.reduce((acc, sprite) => {
            acc[String(sprite && sprite.name || '').toLowerCase()] = sprite;
            return acc;
        }, {});

        const cat = byName.cat || {name: 'Cat', x: 200, y: 190};
        const bird = byName.bird || {name: 'Bird', x: 360, y: 200};

        return {
            introDelayMs: magicShow.introDelayMs || 4800,
            restartPhase: magicShow.restartPhase || PHASES.GROW_TRICK,
            availableBlocks: magicShow.availableBlocks || {},
            idleScaffolding: magicShow.idleScaffolding || {},
            voiceOver: magicShow.voiceOver || {},
            members: {
                cat: {
                    role: 'cat',
                    spriteName: cat.name || 'Cat',
                    speaker: 'cat',
                    homeX: cat.x || 200,
                    homeY: cat.y || 190
                },
                bird: {
                    role: 'bird',
                    spriteName: bird.name || 'Bird',
                    speaker: 'bird',
                    homeX: bird.x || 360,
                    homeY: bird.y || 200
                }
            },
            voiceConfidenceThreshold: 90
        };
    }

    _attachListeners () {
        document.addEventListener('pointerdown', this._onPointerDown, true);
        document.addEventListener('visibilitychange', this._onVisibilityChange);
        window.addEventListener('pagehide', this._onPageHide);
        window.addEventListener('beforeunload', this._onPageHide);
    }

    _handleMeaningfulInteraction () {
        if (this._idlePausedAt) {
            this._idleTimeMs += Math.max(0, Date.now() - this._idlePausedAt);
            this._idlePausedAt = null;
        }

        this._lastInteractionAt = Date.now();
        this._clearIdleTimers();
        this._clearHintVisuals();
        this._startIdleScaffolding();
        if (this._interactionSettleTimer) {
            clearTimeout(this._interactionSettleTimer);
        }
        this._interactionSettleTimer = setTimeout(() => {
            this._interactionSettleTimer = null;
            this._settleInteraction();
        }, 80);
    }

    _settleInteraction () {
        const selectedRole = this._getSelectedRole();
        if (selectedRole && selectedRole !== this._selectedRole) {
            this._selectedRole = selectedRole;
            this._pulseSpriteThumb(selectedRole);
            ActivityAudio.play('sparkle');
        }

        // Re-enforce expected sprite selection — the core editor may
        // default to the player sprite during block drag operations.
        const expected = this._expectedRole();
        if (expected) {
            const currentRole = this._getSelectedRole();
            if (currentRole !== expected) {
                this._selectRole(expected);
            }
        }

        const previous = {...this._scriptSignatures};
        this._refreshScriptSignatures();
        MEMBERS.forEach((role) => {
            if (previous[role] !== this._scriptSignatures[role]) {
                this._handleScriptChange(role, previous[role], this._scriptSignatures[role]);
            }
        });

        this._persistProgress();
    }

    _handleScriptChange (role, previousSignature, nextSignature) {
        if (!nextSignature) {
            return;
        }

        if (this._phase === PHASES.GROW_TRICK && role === 'cat') {
            if (!this._milestones.catFlag && this._roleHasBlock('cat', 'onflag')) {
                this._milestones.catFlag = true;
            }
            if (!this._milestones.catGrow && this._roleHasAnyBlock('cat', ['grow', 'shrink'])) {
                this._milestones.catGrow = true;
                this._queueNarration('VO-END-HINT', null, {speaker: 'cat'});
            }
            if (this._milestones.catGrow && this._roleHasAnyBlock('cat', ['endstack'])) {
                this._transitionTo(PHASES.HIDE_TRICK);
            }
            return;
        }

        if (this._phase === PHASES.HIDE_TRICK && role === 'cat') {
            if (!this._milestones.catHide && this._roleHasBlock('cat', 'hide')) {
                this._milestones.catHide = true;
                this._queueNarration('VO-END-HINT', null, {speaker: 'cat'});
            }
            if (this._milestones.catHide && this._roleHasAnyBlock('cat', ['endstack'])) {
                this._transitionTo(PHASES.SHOW_TRICK);
            }
            return;
        }

        if (this._phase === PHASES.SHOW_TRICK && role === 'cat') {
            if (!this._milestones.catShow && this._roleHasBlock('cat', 'show')) {
                this._milestones.catShow = true;
                this._queueNarration('VO-END-HINT', null, {speaker: 'cat'});
            }
            if (this._milestones.catShow && this._roleHasAnyBlock('cat', ['endstack'])) {
                this._transitionTo(PHASES.BIRD_SAY);
            }
            return;
        }

        if (this._phase === PHASES.BIRD_SAY && role === 'bird') {
            if (!this._milestones.birdSay && this._roleHasBlock('bird', 'say')) {
                this._milestones.birdSay = true;
                this._queueNarration('VO-END-HINT', null, {speaker: 'bird'});
            }
            if (this._milestones.birdSay && this._roleHasAnyBlock('bird', ['endstack'])) {
                this._transitionTo(PHASES.CREATIVE);
            }
            return;
        }
    }

    _validateRun () {
        if (this._phase === PHASES.GROW_TRICK) {
            if (this._roleHasBlock('cat', 'onflag') && this._roleHasAnyBlock('cat', ['grow', 'shrink']) && this._roleHasAnyBlock('cat', ['endstack'])) {
                this._milestones.catGrow = true;
                this._showSpriteBubble('cat', 'I grew!', 2500);
                this._transitionTo(PHASES.HIDE_TRICK);
            }
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (this._phase === PHASES.HIDE_TRICK) {
            if (this._roleHasBlock('cat', 'hide') && this._roleHasAnyBlock('cat', ['endstack'])) {
                this._milestones.catHide = true;
                this._showSpriteBubble('cat', 'Poof!', 2500);
                this._transitionTo(PHASES.SHOW_TRICK);
            }
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (this._phase === PHASES.SHOW_TRICK) {
            if (this._roleHasBlock('cat', 'show') && this._roleHasAnyBlock('cat', ['endstack'])) {
                this._milestones.catShow = true;
                this._showSpriteBubble('cat', 'Ta-da!', 2500);
                this._transitionTo(PHASES.BIRD_SAY);
            }
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (this._phase === PHASES.BIRD_SAY) {
            if (this._roleHasBlock('bird', 'say') && this._roleHasAnyBlock('bird', ['endstack'])) {
                this._milestones.birdSay = true;
                this._showSpriteBubble('bird', 'Amazing!', 2500);
                this._advanceToCreative();
            }
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (this._phase === PHASES.CREATIVE) {
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        this._stateMachine.transition('BUILDING');
        this._persistProgress();
    }

    _advanceFromIntro () {
        this._setPhase(PHASES.GROW_TRICK);
        this._selectRole('cat');
        this._queueNarration('VO-GROW', null, {speaker: 'cat', clear: true});
        this._persistProgress();
    }

    _transitionTo (phase) {
        this._setPhase(phase);
        const voKey = this._voiceOverKeyForPhase(phase);
        if (voKey) {
            this._queueNarration(voKey, null, {speaker: this._speakerForPhase(phase), clear: true});
        }
        if (phase === PHASES.BIRD_SAY) {
            this._selectRole('bird');
        }
        if (phase === PHASES.CREATIVE) {
            this._showDoneButton();
        }
        this._persistProgress();
    }

    _advanceToCreative () {
        this._setPhase(PHASES.CREATIVE);
        this._showSpriteBubble('cat', 'What a show!', 3000);
        this._showSpriteBubble('bird', 'Bravo!', 3000);
        ActivityAudio.play('confetti');
        this._showDoneButton();
        this._queueNarration('VO-BIRD', null, {speaker: 'bird', clear: true});
        this._persistProgress();
    }

    _completeActivity () {
        this._setPhase(PHASES.COMPLETE);
        this._hideDoneButton();
        this._closeAllBubbles();
        this._showSpriteBubble('cat', 'Magic!', 4000);
        this._queueNarration('VO-COMPLETE', null, {speaker: 'cat', clear: true})
            .then(() => {
                if (this._destroyed) {
                    return;
                }
                this._closeAllBubbles();
                this._persistProgress({
                    status: 'completed',
                    stars: 3,
                    bestScore: 3,
                    completedAt: new Date().toISOString()
                });
                this._showCompletionModal();
            });
    }

    _setPhase (phase, opts = {}) {
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
        this._phase = phase;
        this._syncVisualState();
        if (!opts.silent) {
            this._persistProgress();
        }
    }

    _syncVisualState () {
        if (this._paletteRestrictor) {
            this._paletteRestrictor.updateAllowed(this._allowedBlocksForCurrentPhase());
        }

        const expectedRole = this._expectedRole();
        if (expectedRole) {
            this._selectRole(expectedRole);
        }

        this._selectSuggestedCategory();
    }

    _allowedBlocksForCurrentPhase () {
        return (this._config.availableBlocks[this._phase] || []).slice();
    }

    _voiceOverKeyForPhase (phase) {
        switch (phase) {
        case PHASES.GROW_TRICK:
            return 'VO-GROW';
        case PHASES.HIDE_TRICK:
            return 'VO-HIDE';
        case PHASES.SHOW_TRICK:
            return 'VO-SHOW';
        case PHASES.BIRD_SAY:
            return 'VO-BIRD';
        case PHASES.CREATIVE:
            return null;
        default:
            return 'VO-INTRO';
        }
    }

    _startIdleScaffolding () {
        this._clearIdleTimers();
        if (this._phase === PHASES.INTRO || this._phase === PHASES.COMPLETE) {
            return;
        }

        const thresholds = this._config.idleScaffolding[this._phase] || [];
        thresholds.forEach((delay, index) => {
            const tier = index + 1;
            this._idleTimers.push(setTimeout(() => {
                if (this._lastInteractionAt + delay > Date.now()) {
                    return;
                }
                if (tier === 4 && !this._idlePausedAt) {
                    this._idlePausedAt = Date.now();
                }
                this._deliverIdleScaffold(tier);
            }, delay));
        });
    }

    _clearIdleTimers () {
        while (this._idleTimers.length > 0) {
            clearTimeout(this._idleTimers.pop());
        }
    }

    _deliverIdleScaffold (tier) {
        this._hintEvents.push({
            phase: this._phase,
            tier,
            character: this._expectedRole(),
            timestamp: new Date().toISOString()
        });

        if (tier === 1) {
            this._queueNarration(`hint-${this._phase}-1`, this._hintTextForTier(1), {
                speaker: this._speakerForPhase(this._phase),
                clear: true
            });
            return;
        }

        if (tier === 2) {
            this._pulseExpectedTarget();
            return;
        }

        if (tier === 3) {
            this._highlightSuggestedBlock();
            this._queueNarration(`hint-${this._phase}-3`, this._hintTextForTier(3), {
                speaker: this._speakerForPhase(this._phase),
                clear: true
            });
            return;
        }

        if (tier === 4) {
            this._showGhostHand();
        }
    }

    _hintTextForTier (tier) {
        const expected = this._expectedRole();
        if (!this._roleHasBlock(expected, 'onflag')) {
            return tier >= 3
                ? `Start with the green flag block for ${this._displayName(expected)}.`
                : `${this._displayName(expected)} needs the green flag first.`;
        }

        if (this._phase === PHASES.GROW_TRICK) {
            return tier >= 3 ? 'Try the grow block to make Cat bigger!' : 'Can you make Cat grow?';
        }
        if (this._phase === PHASES.HIDE_TRICK) {
            return tier >= 3 ? 'Use the hide block to make Cat disappear!' : 'Time for the disappearing trick!';
        }
        if (this._phase === PHASES.SHOW_TRICK) {
            return tier >= 3 ? 'Use the show block to bring Cat back!' : 'Bring Cat back with the show block!';
        }
        if (this._phase === PHASES.BIRD_SAY) {
            return tier >= 3 ? 'Tap Bird and add a say block!' : 'Bird wants to say something about the show!';
        }
        return tier >= 3
            ? 'Try adding more blocks to make the show even better!'
            : 'Keep experimenting with different blocks!';
    }

    _pulseExpectedTarget () {
        this._clearHintVisuals();
        const expected = this._expectedRole();
        if (expected && this._selectedRole !== expected) {
            this._pulseSpriteThumb(expected);
            return;
        }
        const selector = this._getCategorySelector(this._suggestedCategoryIndex());
        if (selector) {
            selector.classList.add('activity-magic-show-pulse');
        }
    }

    _highlightSuggestedBlock () {
        this._clearHintVisuals();
        const block = this._findPaletteBlock(this._suggestedBlockType());
        if (block) {
            block.classList.add('activity-magic-show-target');
        } else {
            this._pulseExpectedTarget();
        }
    }

    _showGhostHand () {
        this._clearHintVisuals();
        const block = this._findPaletteBlock(this._suggestedBlockType());
        const scripts = gn('scripts');
        if (!block || !scripts) {
            return;
        }

        const blockRect = block.getBoundingClientRect();
        const scriptRect = scripts.getBoundingClientRect();
        this._ghostHand = document.createElement('div');
        this._ghostHand.className = 'activity-magic-show-ghost-hand';
        this._ghostHand.style.left = `${blockRect.left + blockRect.width / 2}px`;
        this._ghostHand.style.top = `${blockRect.top + blockRect.height / 2}px`;
        this._ghostHand.style.setProperty('--activity-magic-show-ghost-dx', `${scriptRect.left + 118 - (blockRect.left + blockRect.width / 2)}px`);
        this._ghostHand.style.setProperty('--activity-magic-show-ghost-dy', `${scriptRect.top + 56 - (blockRect.top + blockRect.height / 2)}px`);
        document.body.appendChild(this._ghostHand);
        setTimeout(() => this._clearHintVisuals(), 1800);
    }

    _clearHintVisuals () {
        document.querySelectorAll('.activity-magic-show-pulse').forEach((el) => el.classList.remove('activity-magic-show-pulse'));
        document.querySelectorAll('.activity-magic-show-target').forEach((el) => el.classList.remove('activity-magic-show-target'));
        document.querySelectorAll('.activity-magic-show-thumb-pulse').forEach((el) => el.classList.remove('activity-magic-show-thumb-pulse'));
        if (this._ghostHand && this._ghostHand.parentNode) {
            this._ghostHand.parentNode.removeChild(this._ghostHand);
            this._ghostHand = null;
        }
    }

    _showDoneButton () {
        if (this._doneBtn) {
            return;
        }

        this._doneBtn = document.createElement('button');
        this._doneBtn.className = 'activity-magic-show-done-btn';
        this._doneBtn.type = 'button';
        this._doneBtn.setAttribute('aria-label', 'Done');
        this._doneBtn.textContent = 'Done';
        this._doneBtn.addEventListener('pointerup', () => {
            this._completeActivity();
        });
        this._container.appendChild(this._doneBtn);
    }

    _hideDoneButton () {
        if (this._doneBtn && this._doneBtn.parentNode) {
            this._doneBtn.parentNode.removeChild(this._doneBtn);
        }
        this._doneBtn = null;
    }

    _showToast (kind, text) {
        if (this._toast && this._toast.parentNode) {
            this._toast.parentNode.removeChild(this._toast);
        }
        this._toast = document.createElement('div');
        this._toast.className = `activity-magic-show-toast activity-magic-show-toast-${kind}`;
        this._toast.textContent = text;
        this._container.appendChild(this._toast);
        setTimeout(() => {
            if (this._toast && this._toast.parentNode) {
                this._toast.parentNode.removeChild(this._toast);
                this._toast = null;
            }
        }, 1500);
    }

    _roleHasBlock (role, blockType) {
        return this._getSpriteBlocks(role).some((block) => block && block.blocktype === blockType);
    }

    _roleHasAnyBlock (role, blockTypes) {
        return this._getSpriteBlocks(role).some((block) => block && blockTypes.includes(block.blocktype));
    }

    _getSpriteBlocks (role) {
        const sprite = this._getSpriteOwner(role);
        const scriptsDiv = sprite ? gn(sprite.id + '_scripts') : null;
        if (!scriptsDiv || !scriptsDiv.owner || typeof scriptsDiv.owner.getBlocks !== 'function') {
            return [];
        }
        return scriptsDiv.owner.getBlocks() || [];
    }

    _getSpriteOwner (role) {
        const targetName = this._config.members[role] && this._config.members[role].spriteName;
        if (!targetName || !ScratchJr.stage || !ScratchJr.stage.currentPage || typeof ScratchJr.stage.currentPage.getSprites !== 'function') {
            return null;
        }
        const spriteIds = ScratchJr.stage.currentPage.getSprites();
        for (let i = 0; i < spriteIds.length; i++) {
            const spriteEl = gn(spriteIds[i]);
            const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
            if (sprite && String(sprite.name).toLowerCase() === String(targetName).toLowerCase()) {
                return sprite;
            }
        }
        return null;
    }

    _getSelectedRole () {
        const page = ScratchJr && ScratchJr.stage ? ScratchJr.stage.currentPage : null;
        // The current-sprite read now goes through the ActivityState observation
        // API (web/ai/activity-state); fall back to the direct ScratchJr read when
        // no observation source is configured (e.g. test contexts without the bridge).
        const observedSpriteId = ActivityState.observe().currentSpriteId;
        const currentSpriteId = observedSpriteId != null ? observedSpriteId : (page ? page.currentSpriteName : null);
        const sprite = ScratchJr.getSprite();
        const spriteName = sprite && sprite.name
            ? String(sprite.name)
            : currentSpriteId && gn(currentSpriteId) && gn(currentSpriteId).owner
                ? String(gn(currentSpriteId).owner.name || '')
                : '';
        if (spriteName) {
            const normalizedName = spriteName.toLowerCase().replace(/\s+\d+$/, '');
            const matchedRole = MEMBERS.find((role) => {
                const targetName = String(this._config.members[role].spriteName || '').toLowerCase();
                return normalizedName === targetName || normalizedName.indexOf(targetName) === 0;
            });
            if (matchedRole) {
                return matchedRole;
            }
        }
        return this._selectedRole || null;
    }

    _selectRole (role) {
        const sprite = this._getSpriteOwner(role);
        if (!sprite || !ScratchJr.stage || !ScratchJr.stage.currentPage) {
            return;
        }
        ScratchJr.stage.currentPage.setCurrentSprite(sprite);
        ScratchJr.stage.currentPage.currentSpriteName = sprite.id;
        ScratchJr.stage.currentPage.lastSprite = sprite.id;
        Thumbs.selectThisSprite(sprite);
        this._selectedRole = role;
    }

    _ensureExpectedSelection () {
        const expected = this._expectedRole();
        if (expected) {
            this._selectRole(expected);
        }
    }

    _expectedRole () {
        switch (this._phase) {
        case PHASES.BIRD_SAY:
            return 'bird';
        case PHASES.CREATIVE:
        case PHASES.COMPLETE:
            return null;
        default:
            return 'cat';
        }
    }

    _speakerForPhase (phase) {
        switch (phase) {
        case PHASES.BIRD_SAY:
            return 'bird';
        case PHASES.GROW_TRICK:
        case PHASES.HIDE_TRICK:
        case PHASES.SHOW_TRICK:
            return 'cat';
        default:
            return 'system';
        }
    }

    _displayName (role) {
        return this._config.members[role] ? this._config.members[role].spriteName : role;
    }

    _selectSuggestedCategory () {
        const index = this._suggestedCategoryIndex();
        if (typeof index === 'number') {
            this._selectCategory(index);
        }
    }

    _suggestedCategoryIndex () {
        const expected = this._expectedRole();
        if (!this._roleHasBlock(expected, 'onflag')) {
            return 0;
        }
        const blockType = this._suggestedBlockType();
        if (blockType === 'grow' || blockType === 'shrink' || blockType === 'hide' || blockType === 'show') {
            return 2;
        }
        if (blockType === 'say') {
            return 2;
        }
        return 1;
    }

    _suggestedBlockType () {
        const expected = this._expectedRole();
        if (!this._roleHasBlock(expected, 'onflag')) {
            return 'onflag';
        }

        switch (this._phase) {
        case PHASES.GROW_TRICK:
            return 'grow';
        case PHASES.HIDE_TRICK:
            return 'hide';
        case PHASES.SHOW_TRICK:
            return 'show';
        case PHASES.BIRD_SAY:
            return 'say';
        default:
            return 'grow';
        }
    }

    _pulseSpriteThumb (role) {
        const thumb = this._getSpriteThumb(role);
        if (thumb) {
            thumb.classList.add('activity-magic-show-thumb-pulse');
        }
    }

    _getSpriteThumb (role) {
        const sprite = this._getSpriteOwner(role);
        return sprite && sprite.thumbnail ? sprite.thumbnail : null;
    }

    _getCategorySelector (index) {
        const selectors = document.getElementById('selectors');
        if (!selectors || typeof index !== 'number') {
            return null;
        }
        return Array.from(selectors.childNodes || []).find((child) => child && child.index === index) || null;
    }

    _findPaletteBlock (blockType) {
        const palette = document.getElementById('palette');
        if (!palette) {
            return null;
        }
        return Array.from(palette.childNodes || []).find((child) => child && child.owner && child.owner.blocktype === blockType) || null;
    }

    _selectCategory (index) {
        if (typeof index !== 'number') {
            return;
        }
        try {
            Palette.selectCategory(index);
        } catch (_) {
            // Palette not ready yet.
        }
    }

    _showSpriteBubble (role, text, durationMs) {
        const sprite = this._getSpriteOwner(role);
        if (!sprite || typeof sprite.openBalloon !== 'function') {
            return;
        }
        sprite.openBalloon(text);
        if (durationMs && durationMs > 0) {
            setTimeout(() => {
                if (sprite && typeof sprite.closeBalloon === 'function') {
                    sprite.closeBalloon();
                }
            }, durationMs);
        }
    }

    _closeSpriteBubble (role) {
        const sprite = this._getSpriteOwner(role);
        if (sprite && typeof sprite.closeBalloon === 'function') {
            sprite.closeBalloon();
        }
    }

    _closeAllBubbles () {
        MEMBERS.forEach((role) => this._closeSpriteBubble(role));
    }

    _showCompletionModal () {
        if (this._activityData && this._activityData.isFreePreview) {
            const premiumCount = this._countPremiumActivities();
            PostPreviewUpsell.show({
                activityTitle: this._activityData.title || 'The Magic Show',
                premiumActivityCount: premiumCount,
                onReplay: () => this.restartActivity(),
                onDismiss: () => {
                    ScratchJr.saveProject(null, ScratchJr.flippage);
                }
            });
            return;
        }

        CompletionModal.show({
            title: 'The Magic Show',
            message: 'You made Cat do magic tricks! Cat can grow, disappear, and come back!',
            onReplay: () => this.restartActivity()
        });
    }

    _dismissCompletionModal () {
        CompletionModal.dismiss();
    }

    _countPremiumActivities () {
        try {
            const curriculum = window.__ACTIVITY_CURRICULUM__;
            if (curriculum && curriculum.units) {
                let count = 0;
                curriculum.units.forEach((unit) => {
                    unit.lessons.forEach((lesson) => {
                        if (lesson.tier === 'premium' && lesson.isFreePreview !== true) {
                            count++;
                        }
                    });
                });
                if (count > 0) {
                    return count;
                }
            }
        } catch (e) {
            // Fall through to default
        }
        return 11;
    }

    _refreshScriptSignatures () {
        MEMBERS.forEach((role) => {
            this._scriptSignatures[role] = this._getSpriteSignature(role);
        });
    }

    _getSpriteSignature (role) {
        return this._getSpriteBlocks(role)
            .map((block) => {
                const argValue = block && block.arg && typeof block.arg.argValue !== 'undefined'
                    ? block.arg.argValue
                    : (block && typeof block.getArgValue === 'function' ? block.getArgValue() : '');
                return `${block.blocktype}:${argValue}`;
            })
            .join('|');
    }

    _getEncodedScripts (role) {
        const sprite = this._getSpriteOwner(role);
        if (!sprite || typeof sprite.getData !== 'function') {
            return [];
        }
        const data = sprite.getData();
        return Array.isArray(data.scripts) ? JSON.parse(JSON.stringify(data.scripts)) : [];
    }

    _setEncodedScripts (role, strips) {
        const sprite = this._getSpriteOwner(role);
        const scriptsDiv = sprite ? gn(sprite.id + '_scripts') : null;
        if (!scriptsDiv || !scriptsDiv.owner) {
            return;
        }
        scriptsDiv.owner.dragList = scriptsDiv.owner.getBlocks();
        scriptsDiv.owner.deleteBlocks();
        scriptsDiv.owner.dragList = [];
        (strips || []).forEach((strip) => {
            scriptsDiv.owner.recreateStrip(strip);
        });
        this._refreshScriptSignatures();
    }

    _restoreSavedScripts (saved) {
        const characterScripts = saved && saved.character_scripts;
        if (!characterScripts || typeof characterScripts !== 'object') {
            return false;
        }

        let restored = false;
        MEMBERS.forEach((role) => {
            const savedScripts = characterScripts[role];
            if (!Array.isArray(savedScripts)) {
                return;
            }
            this._setEncodedScripts(role, savedScripts);
            restored = true;
        });

        if (restored) {
            this._refreshScriptSignatures();
        }

        return restored;
    }

    _queueNarration (id, fallbackText, opts = {}) {
        const text = fallbackText || this._config.voiceOver[id];
        if (!text) {
            return Promise.resolve();
        }

        if (opts.clear) {
            this._stopNarration();
        }

        const runId = this._speechRunId;
        this._lastNarrationId = id;
        this._lastNarrationText = text;
        const entry = {
            id,
            text,
            phase: this._phase,
            speaker: opts.speaker || 'system',
            spokenAt: new Date().toISOString()
        };
        this._voiceHistory.push(entry);
        this._voiceHistory = this._voiceHistory.slice(-30);

        this._speechQueue = this._speechQueue.then(async () => {
            if (this._destroyed || runId !== this._speechRunId) {
                return;
            }
            await this._speakUtterance(text, opts.speaker || 'system');
        });

        return this._speechQueue;
    }

    _stopNarration () {
        this._speechRunId++;
        this._speechQueue = Promise.resolve();
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            try {
                window.speechSynthesis.cancel();
            } catch (_) {}
        }
    }

    _speakUtterance (text, speaker) {
        const preferredVoice = this._getPreferredVoice();
        const settings = this._speakerSettings(speaker);
        if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === 'undefined') {
            return this._wait(Math.min(7200, Math.max(1800, text.split(/\s+/).length * 360)));
        }

        return new Promise((resolve) => {
            let resolved = false;
            let timeoutId = null;
            const finish = () => {
                if (resolved) {
                    return;
                }
                resolved = true;
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                resolve();
            };

            try {
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.lang = preferredVoice && preferredVoice.lang ? preferredVoice.lang : 'en-US';
                if (preferredVoice) {
                    utterance.voice = preferredVoice;
                }
                utterance.rate = settings.rate;
                utterance.pitch = settings.pitch;
                utterance.volume = 1;
                utterance.onend = finish;
                utterance.onerror = finish;
                timeoutId = setTimeout(finish, Math.min(9000, Math.max(2200, text.split(/\s+/).length * 420)));
                window.speechSynthesis.speak(utterance);
            } catch (_) {
                finish();
            }
        });
    }

    _speakerSettings (speaker) {
        switch (speaker) {
        case 'cat':
            return {rate: 0.94, pitch: 1.14};
        case 'bird':
            return {rate: 1, pitch: 1.32};
        default:
            return {rate: 0.92, pitch: 1.08};
        }
    }

    _buildResumePrompt (phase) {
        switch (phase) {
        case PHASES.GROW_TRICK:
            return 'Welcome back to the Magic Show! Help Cat do a growing trick with the green flag and grow block.';
        case PHASES.HIDE_TRICK:
            return 'Welcome back! Now make Cat disappear with the hide block!';
        case PHASES.SHOW_TRICK:
            return 'Welcome back! Bring Cat back with the show block!';
        case PHASES.BIRD_SAY:
            return 'Welcome back! Tap on Bird and make Bird say something about the show!';
        case PHASES.CREATIVE:
            return 'Welcome back! Keep adding blocks to make the show even better, or tap Done when finished!';
        default:
            return this._config.voiceOver['VO-INTRO'];
        }
    }

    _initVoiceSelection () {
        if (!window.speechSynthesis) {
            return;
        }
        this._refreshPreferredVoice();
        if (!this._speechVoice && typeof window.speechSynthesis.addEventListener === 'function') {
            window.speechSynthesis.addEventListener('voiceschanged', this._voicesChangeHandler);
        }
    }

    _waitForPreferredVoice (timeoutMs = 2600) {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return Promise.resolve();
        }
        this._refreshPreferredVoice();
        if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            let resolved = false;
            const finish = () => {
                if (resolved) {
                    return;
                }
                resolved = true;
                resolve();
            };
            const poll = setInterval(() => {
                this._refreshPreferredVoice();
                if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
                    clearInterval(poll);
                    clearTimeout(timeout);
                    finish();
                }
            }, 100);
            const timeout = setTimeout(() => {
                clearInterval(poll);
                finish();
            }, timeoutMs);
        });
    }

    _refreshPreferredVoice () {
        const selection = this._selectPreferredVoice();
        this._speechVoice = selection ? selection.voice : null;
        this._speechVoiceScore = selection ? selection.score : 0;
    }

    _getPreferredVoice () {
        if (!this._speechVoice) {
            this._refreshPreferredVoice();
        }
        return this._speechVoice;
    }

    _selectPreferredVoice () {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return null;
        }
        const voices = window.speechSynthesis.getVoices();
        if (!Array.isArray(voices) || voices.length < 1) {
            return null;
        }
        const ranked = voices
            .map((voice) => ({voice, score: this._scoreVoice(voice)}))
            .sort((a, b) => b.score - a.score);
        return ranked[0] && ranked[0].score > 0 ? ranked[0] : null;
    }

    _scoreVoice (voice) {
        return scorePreferredNarrationVoice(voice, window, {profile: 'magicShow'});
    }

    _wait (ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async _persistProgress (overrides = {}) {
        const now = Date.now();
        const elapsedMs = Math.max(0, now - this._sessionStartedAt - this._idleTimeMs);
        const completedPhase = this._phase === PHASES.COMPLETE || this._phase === PHASES.CREATIVE;
        const status = overrides.status || (completedPhase ? 'completed' : 'in_progress');
        const completedAt = Object.prototype.hasOwnProperty.call(overrides, 'completedAt')
            ? overrides.completedAt
            : (status === 'completed' ? new Date().toISOString() : undefined);
        await ActivityStorageService.saveProgress(this._activityData.activityId, {
            status,
            attempts: overrides.attempts !== undefined ? overrides.attempts : this._flagTapCount,
            stars: overrides.stars !== undefined ? overrides.stars : (status === 'completed' ? 3 : 0),
            bestScore: overrides.bestScore !== undefined ? overrides.bestScore : (status === 'completed' ? 3 : 0),
            hintsUsed: this._hintEvents.length,
            currentState: this._phase,
            totalActiveTimeSec: Math.round(elapsedMs / 1000),
            hintEvents: this._hintEvents.slice(),
            flagTapCount: this._flagTapCount,
            milestones: {...this._milestones},
            character_scripts: {
                cat: this._getEncodedScripts('cat'),
                bird: this._getEncodedScripts('bird')
            },
            completedAt: completedAt,
            lastOpenedAt: new Date().toISOString()
        });
    }

    _resetForReplay () {
        this._clearIdleTimers();
        this._clearHintVisuals();
        this._hideDoneButton();
        this._dismissCompletionModal();
        this._closeAllBubbles();
        this._stopNarration();
        this._flagTapCount = 0;
        this._milestones = {
            catFlag: false,
            catGrow: false,
            catHide: false,
            catShow: false,
            birdSay: false
        };
        this._hintEvents = [];
        this._voiceHistory = [];
        this._sessionStartedAt = Date.now();
        this._lastInteractionAt = Date.now();
        this._idlePausedAt = null;
        this._idleTimeMs = 0;
        ScratchJr.stopStrips();
        ScratchJr.resetSprites();
        MEMBERS.forEach((role) => this._setEncodedScripts(role, []));
        this._setPhase(this._config.restartPhase || PHASES.GROW_TRICK, {silent: true});
        this._selectRole('cat');
        this._refreshScriptSignatures();
        this._syncVisualState();
        this._startIdleScaffolding();
        this._queueNarration('VO-GROW', null, {speaker: 'cat', clear: true});
        this._persistProgress({
            status: 'in_progress',
            completedAt: null
        });
    }
}
